'use client'

import React from 'react'
import { useRouter } from 'next/navigation'

import { signOut, useTenant } from '@rewardops/auth'
import { cn, useIsDesktop, useLocale } from '@rewardops/ui'
import { isValidUrl } from '@rewardops/utils'

import { replacePlaceholders } from '~/utils/translation-utils'
import { useFormat } from '~/components/hooks/use-format'
import { useTranslation } from '~/app/i18n/client'
import { MapleNavigationDropdown } from '../components/navigation-dropdown'
import { UserInitial } from './user-initial'

type ProfileOption = {
  key: string
  value: string
  path?: string
  disabled?: boolean
}

const ProfileOptions: ProfileOption[] = [
  {
    key: 'order-history',
    value: 'nav.myOrders',
    path: '/order-history',
  },
  {
    key: 'auth/sso/logout',
    value: 'nav.logOut',
  },
]

export const PointsBalance = ({
  member,
  color,
}: {
  member: { firstName: string; points: number }
  color?: string
}) => {
  const { formatNumber } = useFormat()
  const router = useRouter()
  const { t } = useTranslation()
  const { locale } = useLocale()
  const tenant = useTenant()
  const isDesktop = useIsDesktop()
  const fontClass = tenant.styleVariant?.header?.links?.weight || ''

  const localizedOptions: ProfileOption[] = [
    ...(tenant?.accountUrl
      ? [
          {
            key: replacePlaceholders(tenant.accountUrl, {
              locale: locale?.toLowerCase(),
            }),
            value: t('nav.myAccount'),
          },
        ]
      : []),
    ...(tenant?.flags?.profilePage
      ? [
          {
            key: `/${locale}/profile`,
            value: t('nav.myProfile'),
          },
        ]
      : []),
    {
      key: `/${locale}/order-history`,
      value: t('nav.myOrders'),
    },
    ...(!tenant?.flags?.logoutDisabled
      ? [
          {
            key: `/${locale}/auth/sso/logout`,
            value: t('nav.logOut'),
          },
        ]
      : []),
  ]

  const onValueChange = async (option: string) => {
    /**
     * if this function returns true then the url is absolute and not relative
     * that means that it's an external url
     *  */
    const accountUrl = replacePlaceholders(tenant?.accountUrl ?? '', {
      locale: locale?.toLowerCase(),
    })
    if (isValidUrl(option) && option == accountUrl) {
      return window.open(option, '_blank')
    }

    if (option?.includes('/logout')) {
      return await signOut({
        redirectUrl: window.location.href,
        logoutEndpoint: tenant?.logoutEndpoint,
      })
    }

    router.push(option || '/')
  }

  if (!member) return null

  const points = formatNumber(member?.points, 0)

  return (
    <div
      className={cn(
        `flex flex-row items-center gap-3 font-semibold ${
          color && `text-${color}`
        }`,
        fontClass,
      )}
    >
      <div className="text-sm font-semibold">
        {t('nav.hello')} {member.firstName}!{' '}
        {tenant?.flags?.showPointsBalance && (
          <>
            <span aria-hidden={true}>
              {replacePlaceholders(t('nav.youHaveBalance'), { points })}
            </span>
            <span className="sr-only">
              {replacePlaceholders(t('nav.youHaveBalanceSr'), { points })}
            </span>
          </>
        )}
      </div>
      {isDesktop && (
        <div aria-label="member dropdown">
          <MapleNavigationDropdown
            id="member-dropdown"
            optionClassName={cn('text-sm font-bold', fontClass)}
            options={localizedOptions}
            onOptionSelect={onValueChange}
            placeholder={
              <UserInitial
                firstName={member.firstName}
                className={
                  color
                    ? `hover:text-${color}`
                    : 'hover:text-tenant-primary-color'
                }
              />
            }
          />
        </div>
      )}
    </div>
  )
}
